import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    IconButton,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataSourcesSection from './settings/DataSourcesSection';
import DataSourceFormDialog from './settings/DataSourceFormDialog';
import DeleteConfirmationDialog from './settings/DeleteConfirmationDialog';
import CompanyProfileSection from './settings/CompanyProfileSection';
import DataSourceService from '../services/DataSourceService';

/**
 * Main Settings component
 */
const Settings = ({ onClose }) => {
    // Estado para las fuentes de datos
    const [dataSources, setDataSources] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Estado para el diálogo de nueva fuente de datos
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentEditId, setCurrentEditId] = useState(null);
    const [isSaving, setIsSaving] = useState(false); // Estado para el loader del formulario
    const [formData, setFormData] = useState({
        name: '',
        type: 'database',
        databaseType: 'postgresql',     // Tipo específico de base de datos
        databaseTypeOriginal: 'postgresql',  // Valor original del tipo de base de datos
        host: '',                // Host real de la base de datos
        hostOriginal: '',        // Valor original del host
        secretName: '',          // Nombre del secreto para AWS Secrets Manager
        database: '',
        databaseOriginal: '',
        databaseVersion: '',
        username: '',
        usernameOriginal: '',
        password: '',
        description: '',
        isPublic: false,
        useSsl: true,
        // Campos para archivos Excel/CSV
        connectionType: '',
        uploadState: 'idle',
        uploadProgress: 0,
        sourceConnectionId: null,
        errorMessage: null,
        csvDelimiter: ','
    });

    // Estado para los errores de validación
    const [formErrors, setFormErrors] = useState({
        name: false,
        host: false,
        database: false,
        username: false,
        password: false
    });

    // Estado para el diálogo de confirmación de eliminación
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [sourceToDelete, setSourceToDelete] = useState(null);

    // Estado para seguimiento de pruebas de conexión
    const [connectingSourceIds, setConnectingSourceIds] = useState([]);
    
    // Estado para prueba de conexión en el formulario
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [testConnectionResult, setTestConnectionResult] = useState(null);
    
    const [expandedSections, setExpandedSections] = useState({
        company: false,
        dataSources: false
    });



    const handleSectionToggle = (section) => (_, isExpanded) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: isExpanded
        }));
    };

    const accordionStyles = {
        backgroundColor: 'var(--wb-bg-elevated)',
        color: 'var(--wb-text-strong)',
        mb: 2,
        borderRadius: 2,
        boxShadow: 'none',
        overflow: 'hidden',
        backgroundImage: 'none',
        '&:before': { display: 'none' }
    };

    const accordionSummaryStyles = {
        px: 2,
        py: 1.5,
        borderBottom: '1px solid var(--wb-overlay-08)',
        '& .MuiAccordionSummary-content': {
            margin: 0
        }
    };

    // Cargar las fuentes de datos al montar el componente
    useEffect(() => {
        fetchDataSources();
    }, []);

    // Función para sanitizar inputs
    const sanitizeInput = (input) => {
        return input ? input.trim() : '';
    };

    // Función para cargar las fuentes de datos desde la API
    const fetchDataSources = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Use real API to fetch data sources
            const sources = await DataSourceService.fetchDataSources();
            setDataSources(sources);

            // If no sources are returned, show empty state
            if (sources.length === 0) {
                console.log('No data sources found');
                // Aquí podrías mostrar un estado vacío en la UI
            }
        } catch (err) {
            console.error('Error loading data sources:', err);
            // Proporcionar un mensaje más descriptivo basado en el tipo de error
            if (err.message && err.message.includes('ResourceNotFoundException')) {
                setError('No se pudo acceder a la tabla de fuentes de datos. La tabla no existe o no tiene acceso a ella.');
            } else {
                setError('No se pudieron cargar las fuentes de datos: ' + err.message);
            }

            // Usar datos de ejemplo si estamos en desarrollo y hay un error de acceso a la tabla
            if (process.env.NODE_ENV === 'development' &&
                err.message && err.message.includes('ResourceNotFoundException')) {
                console.log('Usando datos de ejemplo en modo desarrollo');
                setDataSources([
                    {
                        id: 'example-1',
                        type: 'SQL',
                        name: 'Base de datos de ejemplo',
                        description: 'Esta es una fuente de datos de ejemplo para desarrollo',
                        url: 'ejemplo-secretname',
                        lastSync: new Date().toLocaleString(),
                        status: 'pending'
                    }
                ]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Manejadores para el diálogo
    const handleDialogOpen = async (isEdit = false, sourceData = null) => {
        setIsEditMode(isEdit);
        
        // Reset test connection result when opening dialog
        setTestConnectionResult(null);
        setIsTestingConnection(false);

        if (isEdit && sourceData) {
            try {
                // Obtener los datos actualizados de la fuente
                const updatedSource = await DataSourceService.getDataSourceById(sourceData.id);
                sourceData = updatedSource || sourceData;

                const { id, name, type, url, description } = sourceData;
                setCurrentEditId(id);

                // Convertir el tipo para que coincida con los valores del select
                const formType = type === 'SQL' ? 'database' :
                    type === 'ERP' ? 'erp' :
                        type === 'CRM' ? 'crm' :
                            type === 'Excel' ? 'file' : 'database';

                // Inicialmente configuramos el formulario con los datos básicos
                let formDataInit = {
                    name: name || '',
                    type: formType,
                    host: '',          // El host real se obtendrá del secreto
                    secretName: url || '', // Guardamos el nombre del secreto para referencia
                    database: sourceData.databaseName || '', // Usamos el valor de DynamoDB primero
                    databaseVersion: sourceData.databaseVersion || '',
                    username: '',
                    password: '',
                    description: description || '',
                    isPublic: sourceData.isPublic === true,
                    useSsl: sourceData.useSsl !== false,
                    // File connection fields (Requirement 7.9)
                    file: null,
                    fileName: sourceData.fileName || '',
                    fileSize: 0,
                    fileType: '',
                    hasHeaders: true,
                    sheetName: '',
                    connectionType: '',
                    uploadState: 'idle',
                    uploadProgress: 0,
                    sourceConnectionId: null,
                    errorMessage: null,
                    csvDelimiter: ','
                };

                // Note: Credentials are now stored encrypted in DynamoDB by the backend
                // We don't retrieve them for editing - user must re-enter if changing
                // The backend will preserve existing credentials if not provided in update

                console.log('Datos finales del formulario para edición:', formDataInit);
                setFormData(formDataInit);
            } catch (err) {
                console.error('Error loading source details:', err);
                // Error toast
                setError('No se pudieron cargar los detalles de la fuente');
                return;
            }
        } else {
            // Modo de adición - resetear el formulario
            setCurrentEditId(null);
            setFormData({
                name: '',
                type: 'database',
                databaseType: 'postgresql',  // Tipo específico de base de datos
                databaseTypeOriginal: 'postgresql',  // Valor original del tipo de base de datos
                host: '',
                hostOriginal: '',
                secretName: '',
                database: '',
                databaseOriginal: '',
                databaseVersion: '',
                username: '',
                usernameOriginal: '',
                password: '',
                description: '',
                isPublic: false,
                useSsl: true,
                // Campos para ERP
                erpType: '',
                erpUrl: '',
                // Campos para CRM
                crmType: '',
                crmUrl: '',
                // Campos para archivos Excel/CSV
                file: null,
                fileName: '',
                fileSize: 0,
                fileType: '',
                hasHeaders: true,
                sheetName: '',
                connectionType: '',
                uploadState: 'idle',
                uploadProgress: 0,
                sourceConnectionId: null,
                errorMessage: null,
                csvDelimiter: ','
            });
        }

        // Resetear los errores
        setFormErrors({
            name: false,
            host: false,
            database: false,
            username: false,
            password: false
        });

        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        // No permitir cerrar el diálogo mientras se está guardando
        if (isSaving) return;

        setDialogOpen(false);
        setIsEditMode(false);
        setCurrentEditId(null);
        setIsSaving(false); // Asegurarnos de que el estado del loader se resetee
        
        // Reset test connection result when closing dialog
        setTestConnectionResult(null);
        setIsTestingConnection(false);

        // Reset file upload state fields
        setFormData(prev => ({
            ...prev,
            uploadState: 'idle',
            uploadProgress: 0,
            sourceConnectionId: null,
            errorMessage: null
        }));
    };

    const validateForm = () => {
        const errors = {
            name: !formData.name.trim()
        };

        // Campos específicos según el tipo de fuente
        if (formData.type === 'database') {
            errors.host = !isEditMode && !formData.host.trim() ||
                (isEditMode && formData.host.trim() !== formData.hostOriginal && !formData.host.trim());
            errors.database = !isEditMode && !formData.database.trim() ||
                (isEditMode && formData.database.trim() !== formData.databaseOriginal && !formData.database.trim());
            errors.username = !isEditMode && !formData.username.trim() ||
                (isEditMode && formData.username.trim() !== formData.usernameOriginal && !formData.username.trim());
            errors.password = !isEditMode && !formData.password.trim();
        } else if (formData.type === 'erp') {
            errors.erpType = !formData.erpType;
            errors.erpUrl = !formData.erpUrl;
            errors.username = !formData.username;
            errors.password = !isEditMode && !formData.password.trim();
        } else if (formData.type === 'crm') {
            errors.crmType = !formData.crmType;
            errors.crmUrl = !formData.crmUrl;
            errors.username = !formData.username;
            errors.password = !isEditMode && !formData.password.trim();
        } else if (formData.type === 'file') {
            // Para archivos Excel, el archivo es obligatorio solo en modo de creación
            errors.file = !isEditMode && !formData.file;
        }

        setFormErrors(errors);

        // El formulario es válido si no hay errores (todos los valores son false)
        return !Object.values(errors).some(error => error);
    };

    // Upload flow for file-type data sources
    const handleFileUploadSave = async () => {
        // Capture current formData via functional update pattern using a ref trick
        // We read directly from the formData state variable captured in this closure
        const currentFormData = formData;

        // Step 1: Create source connection record
        let sourceConnectionId;
        try {
            setFormData(prev => ({ ...prev, uploadState: 'uploading', uploadProgress: 0, errorMessage: null }));

            const connectionRecord = await DataSourceService.createFileConnection(currentFormData);
            sourceConnectionId = connectionRecord.source_connection_id || connectionRecord.id;

            if (!sourceConnectionId) {
                throw new Error('No se recibió el ID de la conexión creada');
            }

            setFormData(prev => ({ ...prev, sourceConnectionId }));
        } catch (err) {
            console.error('Error creating file connection record:', err);
            setFormData(prev => ({
                ...prev,
                uploadState: 'error',
                errorMessage: err.message || 'Error al crear el registro de conexión'
            }));
            setIsSaving(false);
            return;
        }

        // Step 2: Get presigned upload URL
        let uploadUrl;
        let s3Key;
        try {
            const contentType = currentFormData.file.type || 'application/octet-stream';
            const uploadUrlData = await DataSourceService.getFileUploadUrl(
                sourceConnectionId,
                currentFormData.fileName,
                contentType
            );
            uploadUrl = uploadUrlData.upload_url || uploadUrlData.url;
            s3Key = uploadUrlData.s3_key || uploadUrlData.key;

            if (!uploadUrl) {
                throw new Error('No se recibió la URL de subida');
            }
        } catch (err) {
            console.error('Error getting file upload URL:', err);
            setFormData(prev => ({
                ...prev,
                uploadState: 'error',
                errorMessage: err.message || 'Error al obtener la URL de subida'
            }));
            setIsSaving(false);
            return;
        }

        // Step 3: XHR PUT to presigned URL with progress tracking
        try {
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', uploadUrl, true);
                xhr.setRequestHeader('Content-Type', currentFormData.file.type || 'application/octet-stream');

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        setFormData(prev => ({ ...prev, uploadProgress: progress }));
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error(`Error al subir archivo: HTTP ${xhr.status}`));
                    }
                };

                xhr.onerror = () => reject(new Error('Error de red al subir el archivo'));
                xhr.ontimeout = () => reject(new Error('Tiempo de espera agotado al subir el archivo'));

                xhr.send(currentFormData.file);
            });
        } catch (err) {
            console.error('Error uploading file to S3:', err);
            setFormData(prev => ({
                ...prev,
                uploadState: 'error',
                errorMessage: err.message || 'Error al subir el archivo'
            }));
            setIsSaving(false);
            return;
        }

        // Step 4: Confirm upload
        setFormData(prev => ({ ...prev, uploadState: 'confirming', uploadProgress: 100 }));
        try {
            await DataSourceService.confirmFileUpload(sourceConnectionId, s3Key);
        } catch (err) {
            console.error('Error confirming file upload:', err);
            setFormData(prev => ({
                ...prev,
                uploadState: 'error',
                errorMessage: err.message || 'Error al confirmar la subida del archivo'
            }));
            setIsSaving(false);
            return;
        }

        // Step 5: Poll for processing status
        setFormData(prev => ({ ...prev, uploadState: 'processing' }));
        const POLL_INTERVAL_MS = 5000;
        const MAX_POLL_SECONDS = 300;
        const startTime = Date.now();

        const pollStatus = async () => {
            try {
                const status = await DataSourceService.pollConnectionStatus(sourceConnectionId);
                console.log('Polling connection status:', status);

                if (status === 'ready' || status === 'active') {
                    // Success — reload data sources and close dialog
                    await fetchDataSources();
                    setIsSaving(false);
                    // Close dialog without calling handleDialogClose (which guards on isSaving)
                    setDialogOpen(false);
                    setIsEditMode(false);
                    setCurrentEditId(null);
                    setTestConnectionResult(null);
                    setIsTestingConnection(false);
                    return;
                }

                if (status === 'error' || status === 'failed') {
                    setFormData(prev => ({
                        ...prev,
                        uploadState: 'error',
                        errorMessage: 'El procesamiento del archivo falló. Por favor intenta nuevamente.'
                    }));
                    setIsSaving(false);
                    return;
                }

                // Check timeout
                const elapsedSeconds = (Date.now() - startTime) / 1000;
                if (elapsedSeconds >= MAX_POLL_SECONDS) {
                    setFormData(prev => ({
                        ...prev,
                        uploadState: 'error',
                        errorMessage: 'El procesamiento está tardando más de lo esperado. Revisa el estado más tarde.'
                    }));
                    setIsSaving(false);
                    return;
                }

                // Still processing — poll again
                setTimeout(pollStatus, POLL_INTERVAL_MS);
            } catch (err) {
                console.error('Error polling connection status:', err);
                setFormData(prev => ({
                    ...prev,
                    uploadState: 'error',
                    errorMessage: err.message || 'Error al verificar el estado del procesamiento'
                }));
                setIsSaving(false);
            }
        };

        setTimeout(pollStatus, POLL_INTERVAL_MS);
    };

    const handleSaveDataSource = async () => {
        if (!validateForm()) {
            return; // Si hay errores, no continuar
        }

        setIsSaving(true); // Activar el loader

        try {
            let secretName = isEditMode ? formData.secretName : '';
            let secretResult = { success: true }; // Por defecto asumimos éxito

            // Solo manejamos secrets para database, erp y crm
            if (['database', 'erp', 'crm'].includes(formData.type)) {
                // Generar un nombre para el secreto si es nuevo
                secretName = isEditMode ? formData.secretName : `${formData.type}-${Date.now()}`;

                // Determinar si es necesario actualizar el secreto
                let needsCredentialsUpdate = false;

                if (formData.type === 'database') {
                    needsCredentialsUpdate = !isEditMode || // Siempre para nuevas fuentes
                        (formData.host && formData.host.trim() !== '' && formData.host.trim() !== formData.hostOriginal) || // Si se cambió el host
                        (formData.username && formData.username.trim() !== '' && formData.username.trim() !== formData.usernameOriginal) || // Si se cambió el usuario
                        (formData.password && formData.password.trim() !== '') || // Si se ingresó una nueva contraseña
                        (formData.database && formData.database.trim() !== '' && formData.database.trim() !== formData.databaseOriginal) || // Si se cambió la base de datos
                        (formData.databaseType !== formData.databaseTypeOriginal); // Si se cambió el tipo de base de datos
                } else {
                    // Para ERP y CRM, siempre actualizamos si es nuevo o si se cambió algún dato de conexión
                    needsCredentialsUpdate = !isEditMode ||
                        (formData.username && formData.username !== formData.usernameOriginal) ||
                        (formData.password && formData.password.trim() !== '');
                }

                if (needsCredentialsUpdate) {
                    // Credentials are now passed directly to the API and encrypted by the backend
                    // No need to manage Secrets Manager from the frontend
                    console.log('Credentials will be encrypted by backend API');
                } else {
                    console.log('Solo actualizando metadatos, sin modificar credenciales');
                }
            } else if (formData.type === 'file') {
                // Para archivos Excel/CSV, usamos el flujo de subida secuencial
                // El flujo gestiona el cierre del diálogo por sí mismo
                handleFileUploadSave();
                return; // No llamar handleDialogClose automáticamente
            }

            // Preparar datos para la API según el tipo de fuente
            let updatedFormData = {
                ...formData,
                connectionStatus: 'pending' // Cambiar el estado a pendiente cuando se edita
            };

            if (['database', 'erp', 'crm'].includes(formData.type)) {
                updatedFormData.secretName = secretName;
            }

            // Para archivos, necesitamos convertir el archivo a base64 o FormData
            if (formData.type === 'file' && formData.file) {
                // Si tenemos un archivo nuevo, preparamos para subirlo
                // Aquí utilizaríamos FormData, pero para este ejemplo estamos simplificando
                updatedFormData.fileData = {
                    name: formData.fileName,
                    size: formData.fileSize,
                    type: formData.fileType,
                    hasHeaders: formData.hasHeaders,
                    sheetName: formData.sheetName
                };

                // Eliminamos el objeto File original para no intentar serializarlo
                delete updatedFormData.file;
            }

            console.log('Datos a guardar en la API:', updatedFormData);

            if (isEditMode) {
                // Actualizar fuente existente utilizando la API real
                const updatedSource = await DataSourceService.updateDataSource(currentEditId, updatedFormData);

                // Asegurarnos de que el estado sea "pending"
                updatedSource.status = 'pending';

                console.log('Fuente de datos actualizada con estado pendiente:', updatedSource);

                // Actualizar el estado local con la respuesta de la API
                setDataSources(prevSources =>
                    prevSources.map(source =>
                        source.id === currentEditId ? updatedSource : source
                    )
                );
            } else {
                // Agregar nueva fuente utilizando la API real
                const newSource = await DataSourceService.createDataSource(updatedFormData);

                // Actualizar el estado local con la nueva fuente
                setDataSources(prev => [...prev, newSource]);
            }

            handleDialogClose();
        } catch (err) {
            console.error('Error saving data source:', err);
            setError('Error al guardar la fuente de datos: ' + (err.message || 'Error desconocido'));
        } finally {
            setIsSaving(false); // Desactivar el loader independientemente del resultado
        }
    };

    // **** ---- HANDLERS ---- ****

    // Manejar cambio de archivo para fuentes tipo Excel o CSV
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea un archivo Excel o CSV
            if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
                setFormErrors({
                    ...formErrors,
                    file: 'Por favor, seleccione un archivo Excel o CSV válido (.xlsx, .xls o .csv)'
                });
                return;
            }

            // Limpiar error si existía
            const newErrors = { ...formErrors };
            delete newErrors.file;
            setFormErrors(newErrors);

            // Determinar connectionType basado en la extensión
            const isCsv = file.name.match(/\.csv$/i);
            const derivedConnectionType = isCsv ? 'csv' : 'excel';

            // Guardar información del archivo en el estado
            setFormData(prev => ({
                ...prev,
                file: file,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                connectionType: derivedConnectionType,
                uploadState: 'idle',
                uploadProgress: 0,
                errorMessage: null
            }));
        }
    };

    // Manejar cambios en los campos del formulario
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Reset test connection result when user changes connection parameters
        if (['host', 'port', 'database', 'username', 'password', 'databaseType', 'useSsl'].includes(name)) {
            setTestConnectionResult(null);
        }

        // Handle boolean values for select fields
        let processedValue = value;
        if (name === 'isPublic' || name === 'useSsl' || name === 'hasHeaders') {
            processedValue = value === 'true' || value === true;
        }

        // Si el campo que se cambió es el tipo de fuente, resetear campos específicos
        if (name === 'type') {
            // Reset test connection result when changing type
            setTestConnectionResult(null);
            
            const baseFormData = {
                ...formData,
                [name]: processedValue
            };

            // Resetear campos según el tipo seleccionado
            if (processedValue === 'database') {
                setFormData({
                    ...baseFormData,
                    // Mantener solo los campos relevantes para bases de datos
                    databaseType: 'postgresql',
                    host: '',
                    database: '',
                    databaseVersion: '',
                    username: '',
                    password: '',
                    // Eliminar campos de otros tipos
                    erpType: '',
                    erpUrl: '',
                    crmType: '',
                    crmUrl: '',
                    file: null,
                    fileName: '',
                    fileSize: 0,
                    fileType: '',
                    hasHeaders: true,
                    sheetName: ''
                });
            } else if (processedValue === 'erp') {
                setFormData({
                    ...baseFormData,
                    // Mantener solo los campos relevantes para ERP
                    erpType: '',
                    erpUrl: '',
                    username: '',
                    password: '',
                    // Eliminar campos de otros tipos
                    databaseType: '',
                    host: '',
                    database: '',
                    databaseVersion: '',
                    crmType: '',
                    crmUrl: '',
                    file: null,
                    fileName: '',
                    fileSize: 0,
                    fileType: '',
                    hasHeaders: true,
                    sheetName: ''
                });
            } else if (processedValue === 'crm') {
                setFormData({
                    ...baseFormData,
                    // Mantener solo los campos relevantes para CRM
                    crmType: '',
                    crmUrl: '',
                    username: '',
                    password: '',
                    // Eliminar campos de otros tipos
                    databaseType: '',
                    host: '',
                    database: '',
                    databaseVersion: '',
                    erpType: '',
                    erpUrl: '',
                    file: null,
                    fileName: '',
                    fileSize: 0,
                    fileType: '',
                    hasHeaders: true,
                    sheetName: ''
                });
            } else if (processedValue === 'file') {
                setFormData({
                    ...baseFormData,
                    // Mantener solo los campos relevantes para archivos Excel/CSV
                    file: null,
                    fileName: '',
                    fileSize: 0,
                    fileType: '',
                    hasHeaders: true,
                    sheetName: '',
                    connectionType: '',
                    uploadState: 'idle',
                    uploadProgress: 0,
                    sourceConnectionId: null,
                    errorMessage: null,
                    csvDelimiter: ',',
                    // Eliminar campos de otros tipos
                    databaseType: '',
                    host: '',
                    database: '',
                    databaseVersion: '',
                    username: '',
                    password: '',
                    erpType: '',
                    erpUrl: '',
                    crmType: '',
                    crmUrl: ''
                });
            }

            // Resetear errores de validación relacionados con el tipo anterior
            setFormErrors({
                name: formErrors.name // Mantener solo el error del nombre si existe
            });

            return;
        }

        // Para otros campos que no son el tipo
        setFormData({
            ...formData,
            [name]: processedValue
        });

        // Limpiar el error para este campo si el usuario está escribiendo
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: false
            });
        }
    };

    // Manejador para prueba de conexión desde el formulario (antes de guardar)
    const handleTestConnectionFromForm = async () => {
        console.log('Testing connection from form with data:', formData);
        
        // Validar que tengamos los datos mínimos necesarios
        if (!formData.host || !formData.username || !formData.database) {
            setTestConnectionResult({
                success: false,
                message: 'Por favor complete todos los campos requeridos'
            });
            return;
        }
        
        // Si estamos en modo edición y no hay contraseña, no podemos probar
        if (isEditMode && !formData.password) {
            setTestConnectionResult({
                success: false,
                message: 'Ingrese la contraseña para probar la conexión'
            });
            return;
        }
        
        setIsTestingConnection(true);
        setTestConnectionResult(null);
        
        try {
            // Preparar los parámetros de conexión para la prueba
            const testParams = {
                connection_type: formData.databaseType || 'mysql',
                host: formData.host,
                port: formData.port || getDefaultPort(formData.databaseType),
                database_name: formData.database,
                username: formData.username,
                password: formData.password,
                use_ssl: formData.useSsl !== false
            };
            
            console.log('Testing connection with params:', { ...testParams, password: '***' });
            
            // Llamar a la API para probar la conexión
            const result = await DataSourceService.testConnectionParameters(testParams);
            
            console.log('Connection test result:', result);
            
            setTestConnectionResult({
                success: result.success,
                message: result.message || (result.success ? 'Conexión exitosa' : 'Error en la conexión')
            });
            
        } catch (err) {
            console.error('Error testing connection from form:', err);
            setTestConnectionResult({
                success: false,
                message: err.message || 'Error al probar la conexión'
            });
        } finally {
            setIsTestingConnection(false);
        }
    };
    
    // Función auxiliar para obtener el puerto por defecto según el tipo de base de datos
    const getDefaultPort = (databaseType) => {
        const defaultPorts = {
            'postgresql': 5432,
            'mysql': 3306,
            'sqlserver': 1433,
            'oracle': 1521,
            'redshift': 5439
        };
        return defaultPorts[databaseType] || 3306;
    };

    // Manejador para prueba de conexión
    const handleTestConnection = async (id) => {
        console.log('Testing connection for:', id);

        // Añadir el id a la lista de fuentes que están conectando
        setConnectingSourceIds(prev => [...prev, id]);

        try {
            let isSuccessful = false;

            // Usar la API real para probar la conexión
            const result = await DataSourceService.testConnection(id);
            isSuccessful = result.success;

            if (isSuccessful) {
                // Si la prueba fue exitosa, obtenemos la fuente actualizada para mostrar el tiempo correcto
                const updatedSource = await DataSourceService.getDataSourceById(id);

                console.log('Fuente actualizada después de prueba exitosa:', updatedSource);

                // Actualizar el estado de la fuente de datos con la fuente actualizada
                setDataSources(prevSources => {
                    const newSources = prevSources.map(source =>
                        source.id === id ? updatedSource : source
                    );
                    console.log('Nuevas fuentes de datos después de la actualización:', newSources);
                    return newSources;
                });
            } else {
                // Si la prueba falló, solo actualizamos el estado
                setDataSources(prevSources =>
                    prevSources.map(source =>
                        source.id === id
                            ? {
                                ...source,
                                status: 'error'
                            }
                            : source
                    )
                );
            }

            console.log(`Connection test for source ${id} finished with result: ${isSuccessful ? 'Success' : 'Error'}`);
        } catch (err) {
            console.error('Error testing connection:', err);

            // Update status to error
            setDataSources(prevSources =>
                prevSources.map(source =>
                    source.id === id
                        ? {
                            ...source,
                            status: 'error',
                            lastSync: new Date().toLocaleString()
                        }
                        : source
                )
            );
        } finally {
            // Eliminar el id de la lista de fuentes que están conectando
            setConnectingSourceIds(prev => prev.filter(sourceId => sourceId !== id));
        }
    };    // Manejador para editar fuente
    const handleEditSource = (id) => {
        console.log('Editing source:', id);
        const sourceToEdit = dataSources.find(source => source.id === id);
        if (sourceToEdit) {
            handleDialogOpen(true, sourceToEdit);
        }
    };

    // Manejador para abrir el diálogo de confirmación de eliminación
    const handleDeleteSource = (id) => {
        console.log('Confirming deletion for source:', id);
        const sourceData = dataSources.find(source => source.id === id);
        setSourceToDelete(sourceData);
        setDeleteConfirmOpen(true);
    };

    // Manejador para confirmar la eliminación
    const confirmDelete = async () => {
        if (sourceToDelete) {
            try {
                console.log('Deleting source:', sourceToDelete.id);

                // Si la fuente tiene un secreto asociado, intentamos eliminarlo también
                if (sourceToDelete.url) {
                    try {
                        // Credentials are stored encrypted in DynamoDB and will be soft-deleted with the connection
                        // No separate cleanup needed

                        console.log('Nota: El secreto asociado se mantiene por seguridad:', sourceToDelete.url);
                    } catch (secretErr) {
                        // No interrumpimos el flujo si hay error con el secreto
                        console.error('Error al manejar secreto durante eliminación:', secretErr);
                    }
                }

                // Usar la API real para eliminar la fuente
                await DataSourceService.deleteDataSource(sourceToDelete.id);

                // Update UI state
                setDataSources(dataSources.filter(source => source.id !== sourceToDelete.id));
                setDeleteConfirmOpen(false);
                setSourceToDelete(null);
            } catch (err) {
                console.error('Error deleting data source:', err);
                setError('No se pudo eliminar la fuente de datos: ' + (err.message || 'Error desconocido'));
            }
        }
    };

    // Manejador para cancelar la eliminación
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setSourceToDelete(null);
    };

    return (
        <Box sx={{
            bgcolor: 'var(--wb-bg-panel)',
            color: 'var(--wb-text-strong)',
            minHeight: '100vh',
            p: { xs: 0, sm: 1, md: 2 }
        }}>
            <Container
                maxWidth="lg"
                sx={{
                    mt: { xs: 1, sm: 2 },
                    px: { xs: 0.5, sm: 2 }
                }}
            >
                {/* Encabezado con botón de volver */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 2, sm: 4 },
                        px: { xs: 1, sm: 0 }
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{ color: 'var(--wb-text-strong)', mr: 2 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                            Configuración
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.7, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Personaliza tu experiencia con WhisperBI
                        </Typography>
                    </Box>
                </Box>

                <Accordion
                    expanded={expandedSections.company}
                    onChange={handleSectionToggle('company')}
                    sx={accordionStyles}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'var(--wb-x-text-b0bec5, #B0BEC5)' }} />}
                        sx={accordionSummaryStyles}
                    >
                        <Typography sx={{ color: 'var(--wb-text-strong)', fontWeight: 500 }}>
                            Datos de la empresa
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <CompanyProfileSection />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expandedSections.dataSources}
                    onChange={handleSectionToggle('dataSources')}
                    sx={accordionStyles}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'var(--wb-x-text-b0bec5, #B0BEC5)' }} />}
                        sx={accordionSummaryStyles}
                    >
                        <Typography sx={{ color: 'var(--wb-text-strong)', fontWeight: 500 }}>
                            Fuentes de datos
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 4,
                                    px: 3,
                                    bgcolor: 'var(--wb-bg-panel)'
                                }}
                            >
                                <CircularProgress size={40} sx={{ color: 'var(--wb-primary)' }} />
                            </Box>
                        ) : error ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 4,
                                    px: 3,
                                    color: 'var(--wb-danger-strong)',
                                    bgcolor: 'var(--wb-bg-panel)'
                                }}
                            >
                                <ErrorOutlineIcon sx={{ mr: 1 }} />
                                <Typography color="inherit">
                                    {error}
                                </Typography>
                            </Box>
                        ) : (
                            <DataSourcesSection
                                dataSources={dataSources}
                                connectingSourceIds={connectingSourceIds}
                                onAddClick={() => handleDialogOpen(false)}
                                onEditClick={handleEditSource}
                                onDeleteClick={handleDeleteSource}
                                onTestConnection={handleTestConnection}
                                showHeader={false}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>

            </Container>

            {/* Diálogo para agregar o editar fuente de datos */}
            <DataSourceFormDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onSave={handleSaveDataSource}
                isEditMode={isEditMode}
                formData={formData}
                formErrors={formErrors}
                handleInputChange={handleInputChange}
                handleFileChange={handleFileChange}
                isSaving={isSaving}
                isSaveDisabled={
                    isSaving ||
                    (formData.type === 'file' && formData.file && formData.file.size > 52428800) ||
                    ['uploading', 'confirming', 'processing'].includes(formData.uploadState)
                }
                onTestConnection={handleTestConnectionFromForm}
                isTestingConnection={isTestingConnection}
                testConnectionResult={testConnectionResult}
            />

            {/* Diálogo de confirmación para eliminar fuente de datos */}
            <DeleteConfirmationDialog
                open={deleteConfirmOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                sourceToDelete={sourceToDelete}
            />
        </Box>
    );
};

export default Settings;
